import {ElementRef, Injectable} from '@angular/core';
import {AvailableDays, DateRange, LastVisitedEvent, MomentDateRange} from '@core/models';
import moment from 'moment';
import {SimplebarAngularComponent} from 'simplebar-angular';
import SimpleBar from 'simplebar';
import {BehaviorSubject} from 'rxjs';
import { Sort } from '@angular/material/sort';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  public lastEventItem: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public defaultCompletedRange: MomentDateRange = this.getDateFilter({
    days: 7,
    formatted: false,
    add: false,
    amount: 0,
  }) as MomentDateRange;
  public defaultScheduledRange: MomentDateRange = this.getDateFilter({
    days: 7,
    formatted: false,
    add: true,
  }) as MomentDateRange;

  static formatTime(seconds: number): string {
    const hours: number = Math.floor(seconds / 3600);
    const minutes: number = Math.floor((seconds % 3600) / 60);

    return (
      ('00' + hours).slice(-2) +
      ':' +
      ('00' + minutes).slice(-2) +
      ':' +
      ('00' + Math.floor(seconds - minutes * 60)).slice(-2)
    );
  }

  alignScroll(
    index: number,
    scrollBarComponent: SimplebarAngularComponent,
    selector: string,
    defaultOffset = 20
  ): void {
    const simpleBar = scrollBarComponent.SimpleBar as SimpleBar;
    const element = document.getElementById(selector + index);
    const formTop = element?.offsetTop;
    simpleBar
      .getScrollElement()
      .scrollTo({top: formTop - defaultOffset, behavior: 'smooth'});
  }

  getDateFilter(
    params: {
      days: number;
      formatted?: boolean;
      add?: boolean;
      amount?: number;
    } = {days: 0, formatted: true, add: false, amount: 1}
  ): DateRange | MomentDateRange {
    let dateRange;
    if (params?.add) {
      dateRange = {
        start: moment().startOf('day').utc(false).add(1, 'd'),
        end: moment().endOf('day').utc(false).add(params.days, 'd'),
      };
    } else if (params.days === 1) {
      dateRange = {
        start: moment().utc(false).subtract(params.days, 'd').startOf('d'),
        end: moment().utc(false).subtract(params.days, 'd').endOf('d'),
      };
    } else {
      dateRange = {
        start: moment().utc(true).subtract(params.days, 'd'),
        end: moment().utc(false).add(params.amount, 'd'),
      };
    }

    return this.processDateRange(params.formatted, dateRange);
  }

  private processDateRange(
    format: boolean,
    dateRange: MomentDateRange
  ): DateRange | MomentDateRange {
    if (format) {
      return {
        start: dateRange.start.format(),
        end: dateRange.end.format(),
      } as DateRange;
    }
    return dateRange as MomentDateRange;
  }

  trimObjectValues<T>(obj): T {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (
          typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])
        ) {
          obj[key] = this.trimObjectValues(obj[key]);
        } else {
          if (typeof obj[key] === 'string') {
            obj[key] = obj[key].trim();
          }
        }
      }
    }
    return obj;
  }

  public sortWeekDays(weekDays: AvailableDays): AvailableDays {
    const weekDayOrder = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

    let sortedWeekDays = Object.entries(weekDays);
    sortedWeekDays = sortedWeekDays.sort((a, b) => weekDayOrder.indexOf(a[0]) - weekDayOrder.indexOf(b[0]));
    let sortedWeekDaysObject = {};
    sortedWeekDays.forEach(item => {
      sortedWeekDaysObject[item[0]] = item[1];
    });

    return sortedWeekDaysObject as AvailableDays;
  }

  public sortData<T = any>(sort: Sort, data: T[], getValue?: (item: T) => any): T[] {
    if (!getValue) { getValue = (item) => item[sort.active]; }

    const copiedData = [...data];
    if (sort.active && sort.direction !== '') {
      copiedData.sort((a, b) => {
        const isAsc = sort.direction === 'asc';
        return this._compare(getValue(a), getValue(b), isAsc);
      });
    }

    return [...copiedData];
  }

  private _compare(a: number | string, b: number | string, isAsc: boolean) {
    const correction = (isAsc ? 1 : -1);

    if ((a === null || a === '') && b !== null) { return -1 * correction; }
    if ((b === null || b === '') && a !== null) { return 1 * correction; }
    if ((b === null || b === '') && (a === null || a === '')) { return 0; }

    a = typeof a === 'string' ? a.toLowerCase() : a;
    b = typeof b === 'string' ? b.toLowerCase() : b;

    if (!isNaN(+a)) { a = +a; }
    if (!isNaN(+b)) { b = +b; }

    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * correction;
    } else {
      a = `${a}`;
      b = `${b}`;
    }

    return (a < b ? -1 : 1) * correction;
  }
}
